import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stack,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Badge,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";

import UsuariosService from "../../../usuarios/api/usuarios.api";
import { esPreparador } from "../../helpers/preparadores";
import { BOTON_PRIMARIO_SX, BOTON_SECUNDARIO_SX, ALTURA_BOTON } from "../../../../components/ui/buttonStyles";
import { getFiltrosInventariosIniciales } from "../../constants";

// DECISION: mismo estilo que FiltrosPeticiones (etiqueta flotante, fila con
// Filtrar/Limpiar, acordeón en móvil) a petición del usuario, aunque CLAUDE.md
// prefiere la etiqueta con Typography encima. Los filtros van al servidor, por
// eso se aplican con el botón. El estado se elige en EstadisticasInventarios
const BOTON_FILTRO_PRIMARIO_SX = { ...BOTON_PRIMARIO_SX, height: ALTURA_BOTON.FILTRO };
const BOTON_FILTRO_SECUNDARIO_SX = { ...BOTON_SECUNDARIO_SX, height: ALTURA_BOTON.FILTRO };

const CampoSelect = ({ etiqueta, value, onChange, opciones }) => (
    <Box sx={{ flex: 1 }}>
        <FormControl fullWidth>
            <InputLabel shrink>{etiqueta}</InputLabel>
            <Select
                displayEmpty
                notched
                value={value}
                onChange={(event) => onChange(event.target.value)}
                label={etiqueta}
                sx={{ backgroundColor: "#fff" }}
            >
                <MenuItem value="">Todos</MenuItem>
                {opciones.map((opcion) => (
                    <MenuItem key={opcion.value} value={opcion.value}>
                        {opcion.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    </Box>
);

const CampoFecha = ({ etiqueta, value, onChange, error }) => (
    <Box sx={{ flex: 1 }}>
        <TextField
            type="date"
            label={etiqueta}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            error={Boolean(error)}
            helperText={error || ""}
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
        />
    </Box>
);

/**
 * Filtros de fecha de creación y preparador de los listados de inventarios.
 * Sin `conPreparador` (Mis inventarios) no pinta ni carga el selector de preparador
 */
const FiltrosInventario = ({ filtros, onAplicar, loading, totalResultados, conPreparador = true }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const [expanded, setExpanded] = useState(true);
    const [formulario, setFormulario] = useState(filtros);
    const [usuarios, setUsuarios] = useState([]);

    useEffect(() => setFormulario(filtros), [filtros]);

    useEffect(() => {
        if (!conPreparador) return undefined;
        let vivo = true;
        UsuariosService.list()
            .then((data) => { if (vivo) setUsuarios(Array.isArray(data) ? data : []); })
            .catch((error) => console.error("Error al cargar preparadores:", error));
        return () => { vivo = false; };
    }, [conPreparador]);

    const opcionesPreparador = useMemo(
        () => usuarios.filter(esPreparador).map((usuario) => ({ value: String(usuario.id), label: usuario.username })),
        [usuarios]
    );

    // Las fechas siempre tienen valor (último mes por defecto): solo cuenta el preparador
    const filtrosActivos = conPreparador && filtros.preparadorId ? 1 : 0;
    const rangoInvalido = Boolean(formulario.fechaDesde && formulario.fechaHasta && formulario.fechaHasta < formulario.fechaDesde);
    const cambiar = (campo) => (valor) => setFormulario((prev) => ({ ...prev, [campo]: valor }));

    const contenido = (
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "flex-start" }}>
            <CampoFecha etiqueta="Fecha inicio" value={formulario.fechaDesde ?? ""} onChange={cambiar("fechaDesde")} />
            <CampoFecha
                etiqueta="Fecha fin"
                value={formulario.fechaHasta ?? ""}
                onChange={cambiar("fechaHasta")}
                error={rangoInvalido ? "Debe ser posterior a fecha inicio" : ""}
            />
            {conPreparador && (
                <CampoSelect
                    etiqueta="Preparador"
                    value={formulario.preparadorId ?? ""}
                    onChange={cambiar("preparadorId")}
                    opciones={opcionesPreparador}
                />
            )}

            <Box sx={{ flex: { xs: 1, md: "0 0 auto" }, width: { md: 120 } }}>
                <Button
                    variant="contained"
                    onClick={() => onAplicar(formulario)}
                    disabled={loading || rangoInvalido}
                    fullWidth
                    startIcon={
                        loading ? (
                            <CircularProgress size={18} sx={{ color: "#fff" }} />
                        ) : (
                            // Sin filtros no se pinta el "0": el botón se anunciaría como "0 Filtrar"
                            <Badge badgeContent={filtrosActivos || null} color="error">
                                <FilterListIcon />
                            </Badge>
                        )
                    }
                    sx={BOTON_FILTRO_PRIMARIO_SX}
                >
                    Filtrar
                </Button>
            </Box>

            <Box sx={{ flex: { xs: 1, md: "0 0 auto" }, width: { md: 120 } }}>
                <Button
                    variant="outlined"
                    onClick={() => onAplicar(getFiltrosInventariosIniciales())}
                    disabled={loading}
                    fullWidth
                    startIcon={<ClearIcon />}
                    sx={BOTON_FILTRO_SECUNDARIO_SX}
                >
                    Limpiar
                </Button>
            </Box>
        </Stack>
    );

    const resultados = totalResultados !== undefined && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: isMobile ? "center" : "right" }}>
            {totalResultados} {totalResultados === 1 ? "resultado" : "resultados"}
        </Typography>
    );

    if (isMobile) {
        return (
            <Accordion
                expanded={expanded}
                onChange={() => setExpanded(!expanded)}
                sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: "8px !important", "&:before": { display: "none" }, mb: 2 }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        backgroundColor: "#f8f9fa",
                        borderRadius: expanded ? "8px 8px 0 0" : "8px",
                        "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 },
                    }}
                >
                    <FilterListIcon sx={{ color: "#cc071e" }} />
                    <Typography sx={{ fontWeight: 600, flex: 1 }}>Filtros</Typography>
                    {filtrosActivos > 0 && <Badge badgeContent={filtrosActivos} color="error" sx={{ mr: 2 }} />}
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 3 }}>
                    {contenido}
                    {resultados}
                </AccordionDetails>
            </Accordion>
        );
    }

    return (
        <Box>
            {contenido}
            {resultados}
        </Box>
    );
};

export default FiltrosInventario;
